/**
 * useADLRecord Hook
 * CCH Care — Phase 3 / Phase 4 → Late Doc window passthrough (2026-05-16)
 *
 * Background-poll data-loss fix (2026-06-18):
 *   The 60s auto-refresh (refreshRecord → hydrate → setSections) was
 *   replacing the editable `sections` data out from under whichever
 *   section form a CNA was mid-edit in. ADLFlowSheetPage passes
 *   sections[key] straight into each form as `initialData`, and the
 *   section forms re-seed their chip/field state from it. When the poll
 *   landed BEFORE an in-progress entry had been saved, it re-seeded from
 *   the server's still-empty copy and silently wiped the typed/selected
 *   values (reproduced: Hygiene chips cleared by the poll; survived only
 *   when the save won the race against the 60s timer).
 *
 *   This is NOT the page-level remount: refreshRecord never flips
 *   `loading`, so the `loading && !record` guard in ADLFlowSheetPage is
 *   irrelevant to it — no remount occurs, the loss is purely the
 *   setSections re-seed.
 *
 *   Fix: split the refresh.
 *     - refreshStatus (NEW)  → used by the 60s background poll. Hydrates
 *       ONLY the status-bearing fields (record, signatures, amendments,
 *       window) so "signed elsewhere", grace→closed, and supervisor-review
 *       changes still surface — but deliberately leaves `sections`
 *       untouched, so a background poll can never overwrite unsaved input.
 *     - refreshRecord (full) → unchanged behaviour; still re-pulls
 *       `sections`. Used only by EXPLICIT user actions (attestation
 *       retract, amendment save, supervisor review, section onDataSaved),
 *       where pulling fresh section data is the intended result. Exposed
 *       publicly as `refresh` exactly as before, so callers don't change.
 *
 * Late Doc window passthrough (2026-05-16):
 * - hydrate() forwards the `window` object from records.php (built by
 *   adl_evaluate_write_window in _base.php) and the hook returns it as
 *   `window`. Previously dropped on the floor: the backend has enforced
 *   the documentation window since late-doc Phase 1 (2026-04-17), but this
 *   hook never surfaced `window`, so ADLFlowSheetPage's grace-period /
 *   window-closed banners and read-only disabling were dead code.
 *   Restoring the passthrough makes the existing server-side enforcement
 *   visible to the user.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { createADLRecord, getADLRecord } from '../api/adl';

const AUTO_REFRESH_MS = 60 * 1000;

const SECTION_KEYS = [
  'hygiene', 'meals', 'repositioning', 'fluid_intake',
  'bowel', 'urination', 'skin_checks', 'safety_checks',
  'vitals',
];

export default function useADLRecord(patientId, careShiftId, shiftType) {
  const [record, setRecord]           = useState(null);
  const [sections, setSections]       = useState({});
  const [signatures, setSignatures]   = useState({});
  const [amendments, setAmendments]   = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [adlRecordId, setAdlRecordId] = useState(null);
  // Late-doc write window from records.php (adl_evaluate_write_window).
  // Internal name avoids shadowing the global `window`; surfaced below
  // on the return object as `window` for ADLFlowSheetPage.
  const [writeWindow, setWriteWindow] = useState(null);

  const fetchRecord = useCallback(async () => {
    if (!patientId || !shiftType) return;
    try {
      setLoading(true);
      setError(null);
      const data = await createADLRecord(patientId, careShiftId, shiftType);
      if (data.success) {
        hydrate(data);
      } else {
        setError(data.error || 'Failed to load ADL record');
      }
    } catch (err) {
      setError(err.message || 'Failed to load ADL record');
    } finally {
      setLoading(false);
    }
  }, [patientId, careShiftId, shiftType]);

  // Full refresh — re-pulls EVERYTHING, including the editable `sections`.
  // Used by explicit user actions (retract / amend / review / onDataSaved),
  // where fresh section data is wanted. NOT used by the background poll.
  // Exposed publicly as `refresh`.
  const refreshRecord = useCallback(async () => {
    if (!adlRecordId) return;
    try {
      const data = await getADLRecord(adlRecordId);
      if (data.success) hydrate(data);
    } catch (err) {
      console.error('ADL record refresh failed:', err.message);
    }
  }, [adlRecordId]);

  // Status-only refresh — used by the 60s background poll. Updates the
  // status-bearing fields so banners / signed-elsewhere / window-state
  // changes surface, but deliberately leaves `sections` untouched so it
  // can never overwrite a CNA's unsaved, in-progress entry.
  // See the background-poll data-loss fix note in the file header.
  const refreshStatus = useCallback(async () => {
    if (!adlRecordId) return;
    try {
      const data = await getADLRecord(adlRecordId);
      if (data.success) hydrateStatus(data);
    } catch (err) {
      console.error('ADL status refresh failed:', err.message);
    }
  }, [adlRecordId]);

  // Full hydrate — replaces all state, INCLUDING editable `sections`.
  function hydrate(data) {
    setRecord(data.record);
    setSections(data.sections     || {});
    setSignatures(data.signatures || {});
    setAmendments(data.amendments || []);
    setAdlRecordId(data.record?.adl_record_id ?? null);
    setWriteWindow(data.window || null);
  }

  // Status-only hydrate — everything EXCEPT `sections`. The omission is
  // the fix: the background poll must not re-seed editable form data.
  function hydrateStatus(data) {
    setRecord(data.record);
    setSignatures(data.signatures || {});
    setAmendments(data.amendments || []);
    setAdlRecordId(data.record?.adl_record_id ?? null);
    setWriteWindow(data.window || null);
    // sections intentionally NOT updated here — see header note.
  }

  useEffect(() => { fetchRecord(); }, [fetchRecord]);

  useEffect(() => {
    if (!adlRecordId) return;
    // Background poll uses the status-only refresh so it can never clobber
    // unsaved in-progress section input.
    const interval = setInterval(refreshStatus, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [adlRecordId, refreshStatus]);

  const sectionStatus = useMemo(() => {
    return SECTION_KEYS.reduce((acc, key) => {
      acc[key] = signatures[key] ? 'signed' : 'draft';
      return acc;
    }, {});
  }, [signatures]);

  const signedCount = useMemo(() => {
    return SECTION_KEYS.filter(k => signatures[k]).length;
  }, [signatures]);

  const completionPct = useMemo(() => {
    return Math.round((signedCount / SECTION_KEYS.length) * 100);
  }, [signedCount]);

  const allSectionsSigned = signedCount === SECTION_KEYS.length;
  const supervisorSigned  = !!signatures['supervisor'];
  const isComplete        = record?.status === 'completed' || record?.status === 'supervisor_reviewed';

  const updateSection = useCallback((sectionKey, newData) => {
    setSections(prev => ({
      ...prev,
      [sectionKey]: Array.isArray(newData)
        ? newData
        : { ...prev[sectionKey], ...newData },
    }));
  }, []);

  const markSectionSigned = useCallback((sectionKey, signatureData) => {
    setSignatures(prev => ({ ...prev, [sectionKey]: signatureData }));
  }, []);

  return {
    record, sections, signatures, amendments, adlRecordId,
    loading, error, sectionStatus, signedCount, completionPct,
    allSectionsSigned, supervisorSigned, isComplete,
    window: writeWindow,
    refresh: refreshRecord, updateSection, markSectionSigned,
  };
}